import type { PapelObraJanelaRequest, PapelObraMarco } from "./papeis-obra.types";
import type { DataLimite } from "./equipe-obra.types";

export const EQUIPE_FIELD_REQUIRED_MESSAGE = "Campo obrigatório";
export const EQUIPE_DATE_ORDER_MESSAGE =
  "A data de fim deve ser igual ou posterior à data de início.";
export const EQUIPE_DATE_RANGE_MESSAGE = "Use datas dentro do período da obra";
export const EQUIPE_OVERLAP_MESSAGE =
  "Este profissional já possui alocação nesta obra em período sobreposto ao informado.";
/** Aviso sob o papel obrigatório cujo prazo já passou: cita o próprio papel. */
export function equipePrazoVencidoMessage(papel: string): string {
  return `O prazo para definir o papel ${papel} terminou.`;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;
const DAY_MS = 86_400_000;

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function toUtc(iso: string): number {
  const match = ISO_DATE.exec(iso);
  if (!match) return Number.NaN;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Soma (ou subtrai) dias a uma data `YYYY-MM-DD`, sem passar por fuso. */
export function addDays(iso: string, days: number): string {
  const date = new Date(toUtc(iso) + days * DAY_MS);
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** `DD/MM/AAAA` a partir de `YYYY-MM-DD`. */
export function formatDataBr(iso: string): string {
  const match = ISO_DATE.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : iso;
}

/** `Date` local do dia (meia-noite) para alimentar o Calendar; `null` se inválida. */
export function isoToDate(iso: string): Date | null {
  const match = ISO_DATE.exec(iso);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function dateToIso(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Meses (`AAAA-MM`) cobertos pela obra, como exige a consulta de profissionais. */
export function periodoDaObra(inicio: string, fim: string): { from: string; to: string } {
  return { from: inicio.slice(0, 7), to: fim.slice(0, 7) };
}

function resolveMarco(
  marco: PapelObraMarco,
  dias: number,
  obraInicio: string,
  obraFim: string,
): DataLimite {
  switch (marco) {
    case "ANTES_INICIO_OBRA":
      return { data: addDays(obraInicio, -dias), rotulo: `D-${dias}` };
    case "DEPOIS_INICIO_OBRA":
      return { data: addDays(obraInicio, dias), rotulo: `D+${dias}` };
    case "ANTES_TERMINO_OBRA":
      return { data: addDays(obraFim, -dias), rotulo: `D-${dias}` };
    case "DEPOIS_TERMINO_OBRA":
      return { data: addDays(obraFim, dias), rotulo: `D+${dias}` };
  }
}

/**
 * Data limite para adicionar o papel (a entrada da janela configurada na HU 2034)
 * aplicada ao período da obra. `inicioDaObra` vale o próprio início (D-0).
 * `null` quando o papel não tem janela.
 */
export function calcularDataLimite(
  janela: PapelObraJanelaRequest | null,
  obraInicio: string,
  obraFim: string,
): DataLimite | null {
  if (!janela) return null;

  const { inicioDaObra, dias, marco } = janela.entrada;
  if (inicioDaObra) return { data: obraInicio, rotulo: "D-0" };
  if (typeof dias !== "number" || !marco) return null;

  return resolveMarco(marco, dias, obraInicio, obraFim);
}

/** Data de saída da janela do papel aplicada à obra; `null` sem janela. */
export function calcularDataSaida(
  janela: PapelObraJanelaRequest | null,
  obraInicio: string,
  obraFim: string,
): string | null {
  if (!janela) return null;

  const { terminoDaObra, dias, marco } = janela.saida;
  if (terminoDaObra) return obraFim;
  if (typeof dias !== "number" || !marco) return null;

  return resolveMarco(marco, dias, obraInicio, obraFim).data;
}

function clamp(value: string, min: string, max: string): string {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/**
 * Período sugerido da alocação: a janela do papel aplicada à obra e recortada
 * ao período da obra. Papéis sem janela sugerem a obra inteira.
 */
export function sugerirPeriodo(
  janela: PapelObraJanelaRequest | null,
  obra: { inicio: string; fim: string },
): { inicio: string; fim: string } {
  const entrada = calcularDataLimite(janela, obra.inicio, obra.fim)?.data ?? obra.inicio;
  const saida = calcularDataSaida(janela, obra.inicio, obra.fim) ?? obra.fim;

  const inicio = clamp(entrada, obra.inicio, obra.fim);
  const fim = clamp(saida, obra.inicio, obra.fim);

  return fim < inicio ? { inicio, fim: obra.fim } : { inicio, fim };
}

/** O prazo venceu quando hoje é posterior à data limite. */
export function isPrazoVencido(dataLimite: string, hoje: string): boolean {
  return hoje > dataLimite;
}

export interface PeriodoErrors {
  inicio?: string;
  fim?: string;
}

/** Validação dos campos de data de uma alocação, antes de qualquer requisição. */
export function validarPeriodoAlocacao(
  periodo: { inicio: string; fim: string },
  obra: { inicio: string; fim: string },
): PeriodoErrors {
  const errors: PeriodoErrors = {};
  const { inicio, fim } = periodo;

  if (!inicio) errors.inicio = EQUIPE_FIELD_REQUIRED_MESSAGE;
  if (!fim) errors.fim = EQUIPE_FIELD_REQUIRED_MESSAGE;

  if (inicio && (inicio < obra.inicio || inicio > obra.fim)) {
    errors.inicio = EQUIPE_DATE_RANGE_MESSAGE;
  }
  if (fim && (fim < obra.inicio || fim > obra.fim)) {
    errors.fim = EQUIPE_DATE_RANGE_MESSAGE;
  }
  // Decisão da P.O.: a mensagem de ordem vale só quando as duas datas estão dentro da obra;
  // se alguma sai do período, fica apenas a genérica.
  if (inicio && fim && !errors.inicio && !errors.fim && fim < inicio) {
    errors.fim = EQUIPE_DATE_ORDER_MESSAGE;
  }

  return errors;
}

interface AlocacaoIntervalo {
  usuarioId: string | null;
  inicio: string;
  fim: string;
}

/** Períodos inclusivos que se tocam em ao menos um dia. */
export function periodosSobrepoem(
  first: { inicio: string; fim: string },
  second: { inicio: string; fim: string },
): boolean {
  return first.inicio <= second.fim && second.inicio <= first.fim;
}

/** O mesmo profissional não pode ter dois períodos sobrepostos na mesma obra. */
export function temSobreposicao(
  candidata: AlocacaoIntervalo,
  outras: readonly AlocacaoIntervalo[],
): boolean {
  if (!candidata.usuarioId || !candidata.inicio || !candidata.fim) return false;

  return outras.some(
    (outra) =>
      outra.usuarioId === candidata.usuarioId &&
      Boolean(outra.inicio) &&
      Boolean(outra.fim) &&
      periodosSobrepoem(candidata, outra),
  );
}
